import { useState, useEffect, forwardRef, useImperativeHandle, memo } from "react"
import { TextField, Button, IconButton, Grid, FormControlLabel, FormControl, RadioGroup, FormLabel, Radio, MenuItem, Card, CardHeader, CardContent, Tooltip } from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import { rootStore } from "stores/root-store"
import AddIcon from "@mui/icons-material/Add"
import { useFormik } from "formik"
import { upperFirst } from "lodash"
import { genId } from "utils/app-helper"

const DimensionItem = memo(({ item, shouldShowDelete, onDeleteClicked, onPropChanged }) => {
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: item,
  })

  const handlePropChanged = (event, prop) => {
    formik.setFieldValue(prop, event.target.value)

    if (onPropChanged) onPropChanged(item.id, prop, event.target.value)
  }

  return (
    <form onSubmit={formik.handleSubmit} style={{ marginTop: 12, marginBottom: 24 }}>
      <Grid container spacing={3}>
        <Grid item md={1} xs={12}>
          <TextField
            fullWidth
            label="Seq"
            value={formik.values.sequence}
            variant="outlined"
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <Tooltip placement="top" title="the unit of measure that these attributes apply to">
            <TextField
              error={Boolean(item.validation.quantity_unit_of_measure)}
              fullWidth
              helperText={item.validation.quantity_unit_of_measure}
              select
              label="Unit of Measure"
              name="quantity_unit_of_measure"
              id="quantity_unit_of_measure"
              onChange={event => handlePropChanged(event, "quantity_unit_of_measure")}
              value={formik.values.quantity_unit_of_measure || ""}
              variant="outlined"
            >
              {rootStore.getOptionsByFilter("QUANTITY_UOM").map(option => (
                <MenuItem id={`quantity_unit_of_measure-${option.value}`} key={option.value} value={option.value}>
                  {`${option.label}-${option.value}`}
                </MenuItem>
              ))}
            </TextField>
          </Tooltip>
        </Grid>
        <Grid item md={3} xs={12}>
          <TextField
            fullWidth
            select
            label="Movement Class"
            name="movement_cls"
            id="movement_cls"
            onChange={event => handlePropChanged(event, "movement_cls")}
            value={formik.values.movement_cls || ""}
            variant="outlined"
          >
            {rootStore.getOptionsByFilter("MOVEMENT_CLASS").map(option => (
              <MenuItem id={`movement_cls-${option.value}`} key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item md={3} xs={12}>
          <Tooltip placement="top" title="Relative Fragility of Item - Please refer to guide">
            <TextField
              fullWidth
              id="protection_value"
              label="Protection Value"
              value={formik.values.protection_value}
              onChange={event => handlePropChanged(event, "protection_value")}
              variant="outlined"
            />
          </Tooltip>
        </Grid>
        <Grid item md={2} xs={12}>
          {shouldShowDelete && (
            <IconButton id="btnDeleteDimension" size="large" onClick={() => onDeleteClicked(item.id)}>
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          )}
        </Grid>

        <Grid item md={1} xs={12}></Grid>
        <Grid item md={3} xs={12}>
          <Tooltip placement="top" title="how many pieces are in this unit of measure compared to sequence 1 - i.e. 1 - bottle, 6 - Innerpack, 24 - case">
            <TextField
              error={Boolean(item.validation.conversion_qty)}
              fullWidth
              helperText={item.validation.conversion_qty}
              label="Conversion Qty"
              name="conversion_qty"
              id="conversion_qty"
              onChange={event => handlePropChanged(event, "conversion_qty")}
              value={formik.values.conversion_qty}
              variant="outlined"
              type="number"
            ></TextField>
          </Tooltip>
        </Grid>
        <Grid item md={3} xs={12}>
          <Tooltip placement="top" title="does this unit of measure need to be packed into additional packaging">
            <FormControl>
              <FormLabel id="treat_as_loose">Treat As Loose</FormLabel>
              <RadioGroup
                row
                aria-labelledby="treat_as_loose"
                name="treat_as_loose"
                id="treat_as_loose"
                value={formik.values.treat_as_loose}
                onChange={event => handlePropChanged(event, "treat_as_loose")}
              >
                <FormControlLabel value="Y" control={<Radio />} label="YES" />
                <FormControlLabel value="N" control={<Radio />} label="NO" />
              </RadioGroup>
            </FormControl>
          </Tooltip>
        </Grid>
        <Grid item md={3} xs={12}>
          <TextField
            error={Boolean(item.validation.weight)}
            fullWidth
            helperText={item.validation.weight}
            label="Weight"
            type="number"
            id="weight"
            value={formik.values.weight}
            onChange={event => handlePropChanged(event, "weight")}
            variant="outlined"
          />
        </Grid>
        <Grid item md={2} xs={12}>
          <TextField
            fullWidth
            label="Weight UoM"
            value={formik.values.weight_unit_of_measure}
            variant="outlined"
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>

        <Grid item md={1} xs={12}></Grid>
        <Grid item md={3} xs={12}>
          <TextField
            error={Boolean(item.validation.length)}
            fullWidth
            helperText={item.validation.length}
            label="Length"
            name="length"
            id="length"
            onChange={event => handlePropChanged(event, "length")}
            value={formik.values.length}
            type="number"
            variant="outlined"
          ></TextField>
        </Grid>
        <Grid item md={3} xs={12}>
          <TextField
            error={Boolean(item.validation.width)}
            fullWidth
            helperText={item.validation.width}
            label="Width"
            name="width"
            id="width"
            onChange={event => handlePropChanged(event, "width")}
            value={formik.values.width}
            type="number"
            variant="outlined"
          ></TextField>
        </Grid>
        <Grid item md={3} xs={12}>
          <TextField
            error={Boolean(item.validation.height)}
            fullWidth
            helperText={item.validation.height}
            label="Height"
            name="height"
            id="height"
            onChange={event => handlePropChanged(event, "height")}
            value={formik.values.height}
            type="number"
            variant="outlined"
          ></TextField>
        </Grid>
        <Grid item md={2} xs={12}>
          <TextField
            fullWidth
            label="Dimension UoM"
            value={formik.values.dimension_unit_of_measure}
            variant="outlined"
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
      </Grid>
    </form>
  )
})

const InventoryDimension = forwardRef(({ curDimensions, isSubmitting }, ref) => {
  const [dimensions, setDimensions] = useState([])

  useEffect(() => {
    if (dimensions.length == 0) {
      setDimensions([...dimensions, createItem()])
    }
  }, [dimensions])

  useEffect(() => {
    if (curDimensions) {
      const result = [...curDimensions]
      result.forEach(p => {
        p.validation = {}
        p.id = genId()
      })
      setDimensions(result)
    }
  }, [curDimensions])

  useImperativeHandle(ref, () => ({
    getDimensionsIfValid() {
      if (validateData()) return dimensions

      return null
    },
  }))

  const getErrorMessageOfProp = (newDimensions, id, prop, val) => {
    const propsRequired = ["quantity_unit_of_measure", "conversion_qty"]
    if (propsRequired.includes(prop) && !val) return `${prop == "conversion_qty" ? "Conversion Qty" : "Unit of Measure"} is required`

    const props = ["length", "width", "height"]
    if (props.includes(prop) && (parseInt(val) > 5 || parseInt(val) < 0)) return `${upperFirst(prop)} should be greater than 0 and not be greater than 5`
    if (prop == "weight" && (parseInt(val) > 2000 || parseInt(val) < 0)) return `${upperFirst(prop)} should be greater than 0 and not be greater than 2000`

    const idx = newDimensions.findIndex(x => x.id == id)
    if (prop == "conversion_qty" && idx > 0 && parseInt(val) <= parseInt(newDimensions[idx - 1][prop])) return `Invalid Conversion Qty`

    if (prop == "quantity_unit_of_measure") {
      for (let i = 0; i < newDimensions.length; i++) {
        if (newDimensions[i].id == id) continue

        if (newDimensions[i][prop] == val) return `${val} already exists`
      }
    }

    return ""
  }

  const onPropChanged = async (id, prop, val) => {
    let newDimensions = [...dimensions]
    const item = newDimensions.find(x => x.id === id)
    if (item) {
      if (item.iuom_id) item.action = "CHANGE"

      item[prop] = val
      item.validation[prop] = getErrorMessageOfProp(newDimensions, id, prop, val)

      setDimensions(newDimensions)
    }
  }

  const onDeleteClicked = id => {
    const newDimensions = [...dimensions]
    const item = newDimensions.find(x => x.id === id)
    if (item) {
      if (item.iuom_id) {
        item.action = "DELETE"
        setDimensions(newDimensions)
      } else {
        setDimensions(newDimensions.filter(x => x.id !== id))
      }
    }
  }

  const validateData = () => {
    let isValid = true
    const newDimensions = [...dimensions].filter(item => item.action !== "DELETE")
    const propsValidation = ["length", "width", "height", "conversion_qty", "quantity_unit_of_measure", "weight"]
    newDimensions.forEach(x => {
      if (!x.validation) {
        x.validation = {}
      }

      propsValidation.forEach(prop => {
        x.validation[prop] = getErrorMessageOfProp(newDimensions, x.id, prop, x[prop])
      })

      if (Object.keys(x.validation).find(key => x.validation[key])) isValid = false
    })

    setDimensions(newDimensions)

    return isValid
  }

  const createItem = () => {
    return {
      iuom_id: "",
      product: "",
      conversion_qty: 1,
      dimension_unit_of_measure: "M",
      protection_value: 0,
      height: 0,
      length: 0,
      movement_cls: "",
      quantity_unit_of_measure: "EA",
      sequence: 1,
      treat_as_loose: "N",
      weight: 0,
      weight_unit_of_measure: "KG",
      width: 0,
      validation: {},
      id: genId(),
    }
  }

  const handleAddNew = () => {
    if (validateData()) {
      const newDimensions = [...dimensions]
      const item = createItem()
      setDimensions([...newDimensions, item])
    }
  }

  return (
    <Card id="cardDimensions">
      <CardHeader
        title="Dimensions"
        action={
          <Button id="btnAddDimension" color="grey" variant="contained" onClick={handleAddNew} disabled={isSubmitting} startIcon={<AddIcon />}>
            Add New
          </Button>
        }
      />
      <CardContent>
        {dimensions
          .filter(x => x.action !== "DELETE")
          .map(x => {
            return <DimensionItem key={x.sequence} item={x} shouldShowDelete={dimensions.length > 1} onDeleteClicked={onDeleteClicked} onPropChanged={onPropChanged} />
          })}
      </CardContent>
    </Card>
  )
})

export default memo(InventoryDimension)
